import React, { createContext, useContext, useState, useMemo } from 'react';

interface OrderNotificationContextType {
  unreadCount: number;
  addUnread: () => void;
  resetUnread: () => void;
}

const OrderNotificationContext = createContext<OrderNotificationContextType>({
  unreadCount: 0,
  addUnread: () => {},
  resetUnread: () => {},
});

export const OrderNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(() => {
    const saved = localStorage.getItem('unreadCount');
    return saved ? parseInt(saved, 10) : 0;
  });

  const addUnread = () => {
    setUnreadCount(prev => {
      const next = prev + 1;
      localStorage.setItem('unreadCount', next.toString());
      return next;
    });
  };

  const resetUnread = () => {
    localStorage.setItem('unreadCount', '0');
    setUnreadCount(0);
  };

  // ✅ Memoize the context value to prevent stale state in consumers like Sidebar
  const contextValue = useMemo(() => ({
    unreadCount,
    addUnread,
    resetUnread
  }), [unreadCount]);

  return (
    <OrderNotificationContext.Provider value={contextValue}>
      {children}
    </OrderNotificationContext.Provider>
  );
};

export const useOrderNotifications = () => {
  const context = useContext(OrderNotificationContext);
  if (!context) {
    throw new Error('useOrderNotifications must be used within an OrderNotificationProvider');
  }
  return context;
};
